import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import { ClientsSearchRequisite } from '../../../../../components/ClientsSearchRequisite'
import AssignedClients from '../../../../../components/ClientsSearchRequisite/AssignedClients/AssignedClients'
import BulkTransferModal from '../../../../../components/ClientsSearchRequisite/BulkTransferModal'
import { IsGranted, ROLE, useAuth } from '../../../auth'
import * as Requests from '../../core/_requests'

const ResuisiteClients = () => {
  const { id } = useParams<{ id: string }>()
  const { currentUser } = useAuth()
  const [refresh, setRefresh] = useState(0)
  const [assignedClientIds, setAssignedClientIds] = useState<number[]>([])
  const [totalClientsCount, setTotalClientsCount] = useState(0)
  const [requisiteName, setRequisiteName] = useState<string>('')
  const [requisiteType, setRequisiteType] = useState<string>('')
  const [transferOpen, setTransferOpen] = useState(false)

  const canTransfer = IsGranted(
    [ROLE.ROLE_CASHIER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER],
    currentUser
  )

  useEffect(() => {
    if (!id) return
    Requests.getRequisiteAPI(id).then((res) => {
      if (res.data?.success && res.data.requisite) {
        setRequisiteName(res.data.requisite.name || '')
        setRequisiteType(res.data.requisite.requisiteType || '')
      }
    })
  }, [id])

  useEffect(() => {
    if (!id) return
    const name = requisiteName || '—'
    const type = requisiteType || '—'
    document.title = `ID: ${id} | Название: ${name} | Тип: ${type}`
    return () => { document.title = 'CHC | Payment system' }
  }, [id, requisiteName, requisiteType])

  const copyId = useCallback(async (value: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value)
        Swal.fire({ toast: true, position: 'top', icon: 'success', title: 'Скопировано', showConfirmButton: false, timer: 2000 })
      } else {
        Swal.fire({ toast: true, position: 'top', icon: 'info', title: 'Скопируйте вручную', showConfirmButton: false, timer: 3000 })
      }
    } catch {
      Swal.fire({ toast: true, position: 'top', icon: 'info', title: 'Скопируйте вручную', showConfirmButton: false, timer: 3000 })
    }
  }, [])

  const handleClientChange = () => setRefresh((prev) => prev + 1)
  const handleTransferSuccess = () => setRefresh((prev) => prev + 1)

  return (
    <div>
      <div className='card sticky-top-tickets mb-7'>
        <div className='card-header border-0 pt-5 pb-2'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Клиенты</span>
          </h3>
          <div className='d-flex align-items-center gap-3 text-muted fs-7'>
            <span
              onClick={() => id && copyId(id)}
              className='text-primary fw-bold cursor-pointer'
              title='Нажмите для копирования'
            >
              ID: {id || '—'}
            </span>
            <span>| Название: {requisiteName || '—'}</span>
            <span>| Тип: {requisiteType || '—'}</span>
          </div>
        </div>
      </div>

      <div className='card'>
        <div className='card-body py-3'>
          <ClientsSearchRequisite
            assignedClientIds={assignedClientIds}
            onClientAdded={handleClientChange}
          />
        </div>
      </div>

      <div className='d-flex justify-content-between align-items-center mt-10'>
        <h3 className='card-label fw-bold fs-3 mb-0'>
          <span>Клиенты связанные с реквизитом</span>
        </h3>
        {canTransfer && (
          <button
            type='button'
            className='btn btn-primary'
            disabled={totalClientsCount === 0}
            onClick={() => setTransferOpen(true)}
          >
            Перенести всех клиентов на другой реквизит
          </button>
        )}
      </div>
      <div className='card mt-5'>
        <div className='card-body py-3'>
          <AssignedClients
            refresh={refresh}
            onClientsChange={setAssignedClientIds}
            onTotalCountChange={setTotalClientsCount}
          />
        </div>
      </div>

      {id && (
        <BulkTransferModal
          open={transferOpen}
          sourceRequisiteId={id}
          onClose={() => setTransferOpen(false)}
          onSuccess={handleTransferSuccess}
        />
      )}
    </div>
  )
}

export default ResuisiteClients
